import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PrivateRoutes from "./privateRoutes";
import { getNewAccessAndRefreshToken } from "../services/jira.service";
import { refreshOpenProjectToken } from "../services/openProject.service";
import { setUserState } from "../store/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { createWorkerInterval } from "../utils/components";
import { requestNotificationPermission } from "../utils/notification";
import { getConfig, getInternalToken } from "../services/auth.service";
import Loader from "../components/loader";
import PublicRoutes from "./publicRoutes";
import { toast } from "react-toastify";

const RootRoutes = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const userState = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(true);
  const [authUser, setAuthUser] = useState<boolean>(false);

  useEffect(() => {
    requestNotificationPermission();

    let workerIntervalForJira: { stop: () => void; }
    let workerIntervalForOpenProject: { stop: () => void; }

    const handleMessageEvent = (event: any) => {
      if (event?.data?.IframeName === "jira-login") {
        console.log('[TEST_JIRA]', event);
        if (event?.data?.error) {
          toast.error(event?.data?.error);
          return;
        }

        if (event?.data?.event === "jiraRefreshEvent") {
          localStorage.setItem('jiraAccessToken', JSON.stringify(event?.data?.data?.access_token));
          localStorage.setItem('jiraRefreshToken', JSON.stringify(event?.data?.data?.refresh_token));
          return
        }

        localStorage.setItem('jiraLoginDetails', JSON.stringify(event?.data?.data?.rawData));
        localStorage.setItem('jiraAccessToken', JSON.stringify(event?.data?.data?.rawData?.access_token));
        localStorage.setItem('jiraRefreshToken', JSON.stringify(event?.data?.data?.rawData?.refresh_token));

        const newUserState = { ...userState.user, jiraDetails: event?.data?.data?.rawData.userDetails }
        dispatch(setUserState(newUserState));

        if (workerIntervalForJira) {
          workerIntervalForJira?.stop();
        }
        // add web work interval 
        workerIntervalForJira = createWorkerInterval(() => {
          getNewAccessAndRefreshToken()
        }, 1000 * 60 * 55)
      }

      if (event?.data?.IframeName === "open-project-login") {
        console.log('[TEST_OPEN_PROJECT]', event);
        if (event?.data?.error) {
          toast.error(event?.data?.error);
          return;
        }

        if (event?.data?.event === "openProjectRefreshEvent") {
          localStorage.setItem('openProjectAccessToken', JSON.stringify(event?.data?.data?.access_token));
          localStorage.setItem('openProjectRefreshToken', JSON.stringify(event?.data?.data?.refresh_token));
          return
        }

        localStorage.setItem('openProjectLoginDetails', JSON.stringify(event?.data?.data?.rawData));
        localStorage.setItem('openProjectAccessToken', JSON.stringify(event?.data?.data?.rawData?.access_token));
        localStorage.setItem('openProjectRefreshToken', JSON.stringify(event?.data?.data?.rawData?.refresh_token));

        const newUserState = { ...userState.user, openProjectDetails: event?.data?.data?.rawData.userDetails }
        dispatch(setUserState(newUserState));

        if (workerIntervalForOpenProject) {
          workerIntervalForOpenProject?.stop();
        }
        // add web work interval 
        workerIntervalForOpenProject = createWorkerInterval(() => {
          refreshOpenProjectToken()
        }, 1000 * 60 * 55)
      }
    }
    window.addEventListener('message', handleMessageEvent);
    // getNewAccessAndRefreshToken()
    return () => {
      window.removeEventListener('message', handleMessageEvent);
      workerIntervalForJira?.stop();
      workerIntervalForOpenProject?.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserDetails = async (id: string): Promise<void> => {
    try {
      setLoader(true);
      if (id) {
        const internalTokenRes = await getInternalToken(id);

        if (internalTokenRes?.statusCode === 200 && internalTokenRes?.data?.internalUUID) {
          const configRes = await getConfig(internalTokenRes?.data?.internalUUID);
          if (configRes?.statusCode === 200 && Object.keys(configRes?.data)?.length > 0) {
            setAuthUser(true);
            setLoader(false);
            sessionStorage.setItem("config", JSON.stringify(configRes?.data?.config));
            sessionStorage.setItem("userId", configRes?.data?.config?.userId || '');
            sessionStorage.setItem("chartId", configRes?.data?.config?.chartId || '');
            return configRes?.data?.config?.email || '';
          } else {
            setAuthUser(false);
            sessionStorage.clear();
            setLoader(false);
            navigate("/notFound");
          }
        } else {
          setAuthUser(false);
          sessionStorage.clear();
          setLoader(false);
          navigate("/notFound");
        }
      } else {
        setAuthUser(false);
        setLoader(false);
        sessionStorage.clear();
        navigate("/notFound");
      }
    } catch (error) {
      setAuthUser(false);
      setLoader(false);
      sessionStorage.clear();
      navigate("/notFound");
    }
  };

  useEffect(() => {
    // sessionStorage.clear();

    let id: string = searchParams.get("id") || "";
    let activity: string = searchParams.get("activity") || "";
    let userList: any = JSON.parse(localStorage.getItem('usernames') || '[]') || [];
    const chartId: string = localStorage.getItem('chartId') || "";
    const userId: string = localStorage.getItem('userId') || "";
    let currentUser: string = searchParams.get('currentUser') || ""
    if (id) {
      sessionStorage.setItem("id", id);
      getUserDetails(id);
    } else if (activity && userList.length) {
      localStorage.removeItem('usernames');
      localStorage.removeItem('chartId');
      localStorage.removeItem('userId');
      setAuthUser(true);
      setLoader(false);
      navigate('/deprecated/userActivity', { state: { users: userList, currentUser: currentUser, chartId, userId } })
    } else {
      setAuthUser(true);
      setLoader(false);
      id = sessionStorage.getItem("id") || "";
      // navigate('/login')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <>
    {loader ? <Loader loader={loader} /> : <>
      {authUser ? <PrivateRoutes /> : <PublicRoutes />}
    </>}
  </>
}

export default RootRoutes